import api from './api';

// Types for the public website
export interface Hero {
  id: number;
  title: string;
  subtitle?: string;
  image_url: string;
  button_text?: string;
  button_url?: string;
  is_active: boolean;
  display_order: number;
}

export interface GalleryItem {
  id: number;
  title: string;
  description?: string;
  image_url: string;
  category?: string;
  is_active: boolean;
  display_order: number;
}

export interface MenuItemOption {
  id: number;
  menu_item_id: number;
  name: string;
  price: number;
  display_order: number;
}

export interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price: number | null;
  image_url?: string;
  category: string;
  name_en?: string;
  description_en?: string;
  category_en?: string;
  is_available: boolean;
  is_featured: boolean;
  has_options: boolean;
  options?: MenuItemOption[];
  display_order: number;
}

export interface MenuCategory {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  name_en?: string;
  description_en?: string;
  display_order: number;
  is_active: boolean;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  author: string;
  author_id: number;
  is_published: boolean;
  published_at?: string;
  reading_time: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface BlogTag {
  id: number;
  name: string;
  slug: string;
}

export interface Popup {
  id: number;
  title?: string;
  description?: string;
  image_url: string;
  url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Service for fetching content from the CMS API
 */
const contentService = {
  /**
   * Get active hero images
   */
  getHeroes: async (): Promise<Hero[]> => {
    const response = await api.get('/hero?active=true');
    return response.data;
  },

  /**
   * Get gallery items with optional filtering
   */
  getGalleryItems: async (category?: string): Promise<GalleryItem[]> => {
    const params = category ? { category, active: true } : { active: true };
    const response = await api.get('/gallery', { params });
    return response.data;
  },

  /**
   * Get gallery categories
   */
  getGalleryCategories: async (): Promise<string[]> => {
    const response = await api.get('/gallery/categories');
    return response.data;
  },

  /**
   * Get menu items with optional filtering
   */
  getMenuItems: async (params?: {
    category?: string;
    available?: boolean;
    featured?: boolean;
  }): Promise<MenuItem[]> => {
    const queryParams = { ...params, available: true };
    const response = await api.get('/menu/items', { params: queryParams });
    return response.data;
  },

  /**
   * Get menu categories
   */
  getMenuCategories: async (): Promise<MenuCategory[]> => {
    const response = await api.get('/menu/categories?active=true');
    return response.data;
  },

  /**
   * Get published blog posts
   */
  getBlogPosts: async (params?: {
    tag?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ posts: BlogPost[]; total: number; page: number; totalPages: number }> => {
    const queryParams = { ...params, published: true };
    const response = await api.get('/blog/posts', { params: queryParams });
    return response.data;
  },

  /**
   * Get a specific blog post by slug
   */
  getBlogPost: async (slug: string): Promise<BlogPost> => {
    const response = await api.get(`/blog/posts/${slug}`);
    return response.data;
  },

  /**
   * Get blog tags
   */
  getBlogTags: async (): Promise<BlogTag[]> => {
    const response = await api.get('/blog/tags');
    return response.data;
  },

  /**
   * Get active popups
   */
  getPopups: async (): Promise<Popup[]> => {
    const response = await api.get('/popup?active=true');
    return response.data;
  },
};

export default contentService; 